import { Button } from '@components/button/Button';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { Option, SearchTokenInput } from '@components/form';
import { Download } from '@components/icons/FarseerIcons';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { MenuButton } from '@components/menu-button/MenuButton';
import { useI18nContext } from '@i18n/i18n-react';
import { ReactNode, useCallback, useState } from 'react';
import {
    getUserGroupKey,
    getUserKey,
    parseUserAndGroupKeys,
    PUBLIC_LINK_KEY,
    useUserAndGroupOptions,
} from './hooks/useUserAndGroupOptions';
import { ShareFilter } from './types';

type ShareTableSearchProps = {
    value: ShareFilter;
    onChange: (v: ShareFilter) => void;

    publicLink?: boolean;

    adding?: boolean;
    exporting?: boolean;
    applying?: boolean;

    onAdd?: () => void;
    onExport?: () => void;
};

const matchOption = (o: Option<string>, q: string) => !!o.label.toLowerCase().includes(q);

export const ShareTableSearch = (props: ShareTableSearchProps) => {
    const { value, onChange, publicLink, adding, exporting, applying, onAdd, onExport } = props;
    const { LL } = useI18nContext();

    const { allOptions } = useUserAndGroupOptions({ publicLink });

    const [query, setQuery] = useState('');
    const values = [...value.userIds.map(getUserKey), ...value.userGroupIds.map(getUserGroupKey)];
    if (value.publicLink) values.push(PUBLIC_LINK_KEY);

    const handleChange = useCallback(
        (s: string[]) => {
            onChange?.(parseUserAndGroupKeys(s));
        },
        [onChange]
    );

    const menuActions: ReactNode[] = [];

    if (onExport) {
        menuActions.push(
            <ContextMenu.Item
                key="export"
                item={{
                    label: LL.EXPORT_SHARES_TO_XLS(),
                    icon: Download,
                    action: onExport,
                }}
            />
        );
    }

    const menuButton = menuActions.length ? (
        <MenuButton
            loading={exporting || applying}
            render={(props) => (
                <ContextMenu {...props.menuState} anchorRef={props.clickAnchorRef}>
                    {menuActions}
                </ContextMenu>
            )}
        />
    ) : null;

    return (
        <FlexCol $gap={8} $minWidth={0} $maxWidth="100%">
            <FlexRow $gap={8}>
                <SearchTokenInput
                    maxItems={10}
                    placeholder={onAdd ? LL.SEARCH_OR_ADD_USERS_GROUPS() : LL.SEARCH_FOR_USERS_GROUPS()}
                    options={allOptions}
                    query={query}
                    values={values}
                    match={matchOption}
                    onQuery={setQuery}
                    onChange={handleChange}
                    onCommit={onAdd}
                />
                {onAdd ? <Button title={LL.ADD()} onClick={onAdd} loading={adding} /> : null}
                {menuButton}
            </FlexRow>
        </FlexCol>
    );
};
